import React from 'react'
import Popover from '@mui/material/Popover';
import GoToClassRoomButton from '../../attendance/vertIcon/GoToClassRoomButton';
import { Button, Box, Tooltip, Typography } from '@mui/material';
import GoToOneDriveButton from './GoToOneDriveButton';
import OneDriveSetupButton from './OneDriveSetupButton';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
// import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'; // commented out - not urgent for now
import Axios from 'axios';
import TextbookPageSetupButton from './TextbookPageSetupButton';
import CourseMemoButtonModal from './CourseMemoButtonModal';
import CourseCancelMemoButtonModal from './CourseCancelMemoButtonModal';
import { getAuthHeader } from '../../../utils/authToken';


const CourseButtonMenu = ({
    userId,
    zoom_id,
    date,
    _time,
    event_idx,
    event,
    enqueueSnackbar,
    colors,
    socketConnected,
    socket,
    classLogUpdatedEventId,
    setClassLogUpdatedEventId,
    testCompletionUpdatedCourseUuid,
    setTestCompletionUpdatedCourseUuid,
    isThisAdminInViewMode,
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    // pace data is seeded from the weekly events response (event.pace) to avoid N+1 /pace requests on mount;
    // it is refreshed via the /pace endpoint only when this event's class log is updated via socket.
    const [hasProblem, setHasProblem] = React.useState(event.pace?.hasProblem ?? null); // for course pace problem
    const [latestLog, setLatestLog] = React.useState(event.pace?.latestLog ?? null);
    const [latestTitle, setLatestTitle] = React.useState(event.pace?.latestTitle ?? null);
    const [previousLog, setPreviousLog] = React.useState(event.pace?.previousLog ?? null);
    const [previousTitle, setPreviousTitle] = React.useState(event.pace?.previousTitle ?? null);
    const [totalPageNotSet, setTotalPageNotSet] = React.useState(false);

    // for test completion summary (commented out - not urgent for now)
    // const [testSummary, setTestSummary] = React.useState(null);

    React.useEffect(() => {
        if (event.course && event.course.textbook_total_page !== null && event.course.textbook_total_page !== 0) {
            setTotalPageNotSet(false);
        } else if (event.course && (event.course.textbook_total_page === null || event.course.textbook_total_page === 0)) {
            setTotalPageNotSet(true);
        }
    }, [event, event.course]);

    async function hasProblemWithCoursePace() {        
        await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${event.course.uuid}/events/${event.id}/pace`, {
            headers: {
                Authorization: getAuthHeader(),
            }
        }).then((res) => {
            if (res.data.hasProblem === true) {
                setHasProblem(true);
            } else {
                setHasProblem(false);
            }

            setLatestLog(res.data.latestLog);
            setLatestTitle(res.data.latestTitle || null);
            setPreviousLog(res.data.previousLog || null);
            setPreviousTitle(res.data.previousTitle || null);
        }).catch((err) => {
            console.log(err);
        });
    }

    // Initial pace data is seeded from event.pace (provided by POST /weekly/zooms/events).
    // No mount-time /pace fetch — that would be one request per visible event.
    // Refresh still happens via socket when this specific event's class log changes (effect below).
    React.useEffect(() => {
        if (event.pace) {
            setHasProblem(event.pace.hasProblem ?? null);
            setLatestLog(event.pace.latestLog ?? null);
            setLatestTitle(event.pace.latestTitle ?? null);
            setPreviousLog(event.pace.previousLog ?? null);
            setPreviousTitle(event.pace.previousTitle ?? null);
        }
    }, [event.pace]);

    // Test summary fetch function (commented out - not urgent for now)
    // async function fetchTestSummary() {
    //     if (!event.course?.uuid) return;
    //     try {
    //         const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${event.course.uuid}/tests/summary`, {
    //             headers: {
    //                 Authorization: getAuthHeader(),
    //             }
    //         });
    //         setTestSummary(res.data);
    //     } catch (err) {
    //         // Silently fail - test summary is optional
    //         console.log('Test summary fetch error:', err);
    //     }
    // }

    React.useEffect(() => {
        if (classLogUpdatedEventId === event.id) {
            hasProblemWithCoursePace();
            setClassLogUpdatedEventId(null);
        }
    }, [classLogUpdatedEventId]);

    // Refetch test summary when a student completes a test for this course (commented out - not urgent for now)
    // React.useEffect(() => {
    //     if (testCompletionUpdatedCourseUuid && testCompletionUpdatedCourseUuid === event.course?.uuid) {
    //         fetchTestSummary();
    //         setTestCompletionUpdatedCourseUuid(null);
    //     }
    // }, [testCompletionUpdatedCourseUuid]);

    const handleButtonClick = (e) => {
        setAnchorEl(e.currentTarget);

        // need to emit to server to let other users know that this user is viewing the course
        socket.emit("put.activeUsers", {
            admin_uuid: userId,
            zoom_id: zoom_id,
            date: date,
            _time: _time,
            event_idx: event_idx,
            active: true,
        });
    };

    const handlePopOverClose = () => {
        setAnchorEl(null);

        // need to emit to server to let other users know that this user is no longer viewing the course
        socket.emit("put.activeUsers", {
            admin_uuid: userId,
            zoom_id: zoom_id,
            date: date,
            _time: _time,
            event_idx: event_idx,
            active: false,
        });
    };

    const popoverOpen = Boolean(anchorEl);
    const popoverId = popoverOpen ? 'popover' : undefined;

    function getFontSize(start_end_diff) {
        if (!start_end_diff || start_end_diff === "1.00") {
            return "8px";
        } else {
            return "12px";
        }
    }

    // Determine test completion indicator color (commented out - not urgent for now)
    // const getTestIndicatorColor = () => {
    //     if (!testSummary || testSummary.totalTests === 0) return null;
    //     const { summary, enrolledStudents } = testSummary;
    //     if (!enrolledStudents || enrolledStudents === 0) return 'disabled'; // No students enrolled
    //     if (summary.completed === summary.total && summary.total > 0) return 'success'; // All completed
    //     if (summary.completed > 0) return 'warning'; // Some completed
    //     return 'disabled'; // None completed
    // };

    // const testIndicatorColor = getTestIndicatorColor();

    const renderPaceTooltip = (showTotalPage) => {
        return (
            <>
                {latestTitle && (
                    <Typography sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{latestTitle} (Latest)</Typography>
                )}
                {latestLog && latestLog.split("\n").map((line, idx) => (
                    <Typography key={`latest-${idx}`}>
                        {line}{showTotalPage ? ` / ${event.course.textbook_total_page}` : ''}
                    </Typography>
                ))}
                {previousLog && (
                    <>
                        <Typography sx={{ borderTop: '1px solid rgba(255,255,255,0.3)', my: 0.5 }} />
                        {previousTitle && (
                            <Typography sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{previousTitle} (Previous)</Typography>
                        )}
                        {previousLog.split("\n").map((line, idx) => (
                            <Typography key={`prev-${idx}`}>
                                {line}{showTotalPage ? ` / ${event.course.textbook_total_page}` : ''}
                            </Typography>
                        ))}
                    </>
                )}
            </>
        );
    };

    return (<>
        {/* Test completion indicator - left side (commented out - not urgent for now) */}
        {/* {testSummary && testSummary.totalTests > 0 && !event.is_canceled_int > 0 && (
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Tooltip
                    title={
                        <Box sx={{ maxWidth: 350 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Tests: {testSummary.totalTests} | Students: {testSummary.enrolledStudents || 0}
                            </Typography>
                            {testSummary.tests.map((test, idx) => {
                                const incomplete = test.students?.filter(s => !s.completed) || [];
                                const isAllComplete = incomplete.length === 0 && test.enrolledStudents > 0;
                                const dueDate = test.dueDate ? new Date(test.dueDate) : null;
                                const isOverdue = dueDate && dueDate < new Date() && !isAllComplete;
                                const dueDateStr = dueDate ? dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null;
                                // Green = completed, Blue = upcoming, Red = overdue
                                const dueDateColor = isAllComplete ? '#4caf50' : (isOverdue ? '#f44336' : '#90caf9');
                                return (
                                    <Box key={idx} sx={{ mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {test.title || `Test ${idx + 1}`}: {test.completedCount}/{test.enrolledStudents || 0}
                                            {isAllComplete && ' ✓'}
                                            {dueDateStr && (
                                                <span style={{ color: dueDateColor, marginLeft: 8, fontSize: '0.85em' }}>
                                                    Due: {dueDateStr}{isOverdue && ' ⚠'}
                                                </span>
                                            )}
                                        </Typography>
                                        {incomplete.length > 0 && (
                                            <Typography variant="caption" sx={{ color: '#ffb74d', display: 'block', pl: 1 }}>
                                                Missing: {incomplete.map(s => s.name).join(', ')}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    }
                    arrow
                    placement='top'
                >
                    <QuizOutlinedIcon color={testIndicatorColor} fontSize='small' />
                </Tooltip>
            </Box>
        )} */}

        {/* Course pace indicator - right side */}
        {!event.course.course_pace_alert_off && hasProblem && !event.is_canceled_int > 0 &&
            <Box
                sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Tooltip
                    title={latestLog ? renderPaceTooltip(true) : ''}
                    arrow
                    placement='top'
                >
                    <AssignmentLateOutlinedIcon color='warning' fontSize='medium' />
                </Tooltip>
            </Box>
        }
        {!event.course.course_pace_alert_off && hasProblem !== null && hasProblem === false &&
            <Box
                sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Tooltip
                    title={latestLog ? renderPaceTooltip(true) : ''}
                    arrow
                    placement='top'
                >
                    <AssignmentLateOutlinedIcon color='success' fontSize='medium' />
                </Tooltip>
            </Box>
        }
        {!event.course.course_pace_alert_off && hasProblem !== null && hasProblem === true && totalPageNotSet &&
            <Box
                sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Tooltip
                    title={
                        <>
                            {latestLog ? renderPaceTooltip(false) : null}
                            <Typography sx={{ borderTop: '1px solid rgba(255,255,255,0.3)', my: 0.5 }} />
                            <Typography>
                                Total Page Not Set!
                            </Typography>
                        </>
                    }
                    arrow
                    placement='top'
                >
                    <AssignmentLateOutlinedIcon color='error' fontSize='medium' />
                </Tooltip>
            </Box>
        }
        <Button
            sx={{
                fontWeight: 800,
                fontSize: getFontSize(event.start_end_diff),
                textAlign: "center",
                width: "100%",
                height: "100%",
                color: event.is_canceled_int ? "red !important" : "#0470C0 !important",
                padding: (!event.start_end_diff || event.start_end_diff === "1.00") ? "0px" : "6px 8px",
                textTransform: "none",
            }}
            disabled={isThisAdminInViewMode}
            onClick={handleButtonClick}
        >
            {event.course?.course_title}
        </Button>
        <Popover
            id={popoverId}
            open={popoverOpen}
            anchorEl={anchorEl}
            onClose={handlePopOverClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "background.paper",
                    width: "200px",
                    boxShadow: 4,
                }}
            >
                <GoToClassRoomButton course={event.course} />
                <TextbookPageSetupButton
                    course={event.course}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                {event.course && event.course.one_drive_url !== null ? <GoToOneDriveButton course={event.course} colors={colors} /> : null}
                <OneDriveSetupButton 
                    course={event.course}
                    colors={colors} 
                    handlePopOverClose={handlePopOverClose} 
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <CourseMemoButtonModal
                    course={event.course}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
                <CourseCancelMemoButtonModal
                    course={event.course}
                    colors={colors}
                    handlePopOverClose={handlePopOverClose}
                    enqueueSnackbar={enqueueSnackbar}
                    socketConnected={socketConnected}
                    socket={socket}
                />
            </Box>
        </Popover>
    </>)
}
export default CourseButtonMenu